import React, { FunctionComponent, RefObject, MouseEvent } from 'react';
import { connect } from 'react-redux';
import {
  partition,
  sort,
  ascend,
  prop,
  concat,
  addIndex,
  map,
  without
} from 'ramda';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { QuestionType } from '../../../../common/enums/QuestionType';
import { setSelectedQuestionsRoutine } from '../../routines';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';

interface IProps {
  selectedQuestions: ISurveyQuestion[];
  questionLoading: boolean;
  containerRef: RefObject<HTMLElement>;
  setSelectedQuestions: IBindingCallback1<ISurveyQuestion[]>;
  openQuestionBank: IBindingAction;
}

const DraggableQuestionTable: FunctionComponent<IProps> = ({
  selectedQuestions,
  questionLoading,
  containerRef,
  setSelectedQuestions,
  openQuestionBank
}) => {
  const [properQuestions, others] = partition(
    ({ question }) => question.type !== QuestionType.RatingScale,
    selectedQuestions
  );
  const orderedQuestions = sort(ascend(prop<string>('order')), properQuestions);

  const updateSelectedQuestions = (newSelected: ISurveyQuestion[]) => {
    const newSelectedOrdered = addIndex<ISurveyQuestion>(map)((item, order) => ({ ...item, order }), newSelected);
    setSelectedQuestions(concat(newSelectedOrdered, others));
  };

  const onCellClicked = ({ accessor }: IRowAccessor<ISurveyQuestion>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'sendTo':
      case 'remove':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  const onRemoveQuestion = (question: ISurveyQuestion) => {
    const newSelected = without([question], orderedQuestions);
    updateSelectedQuestions(newSelected);
  };

  const onChangeReference = (questionId: string, sendTo: ReferenceType[]) => {
    const updatedQuestions = map(question => (question.question.id === questionId
      ? { ...question, sendTo }
      : question
    ), selectedQuestions);

    setSelectedQuestions(updatedQuestions);
  };

  return (
    <div className="popup-wrapper">
      <Table
        config={{
          columns: getColumns(containerRef, { onChangeReference, onRemove: onRemoveQuestion }),
          data: { items: orderedQuestions, total: orderedQuestions.length },
          onCellClicked,
          defaultSorted: [{ id: 'order', desc: false }],
          itemKey: (data: ISurveyQuestion) => data.question.id,
          autoHeight: true,
          draggable: true,
          loading: questionLoading,
          isRowDraggable: (data: ISurveyQuestion) => !data.question.isRequired,
          onDragDrop: (items: ISurveyQuestion[]) => updateSelectedQuestions(items),
          noDataPlaceholder: <NoDataPlaceholder onButtonClick={openQuestionBank} variant="table" />,
          noDataPlaceholderHeight: 400
        }}
      />
    </div>
  );
};

const mapStateToProps = ({
  surveyBuilder: { selectedQuestions, questionLoading }
}: IAppState) => ({ selectedQuestions, questionLoading });

const mapDispatchToProps = {
  setSelectedQuestions: setSelectedQuestionsRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(DraggableQuestionTable);
